import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";
import { Header } from "./Header";
import { BottomNavigation } from "./BottomNavigation";
import { Tutorial } from "./Tutorial";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import { Analytics } from "@/lib/analytics";
import { SAFE_AREA } from "@/utils/safeArea";

export const Layout = () => {
  const { profile, loading } = useProfile();
  const location = useLocation();
  const [showTutorial, setShowTutorial] = useState(false);
  const keyboardHeight = useKeyboardHeight();
  
  // Enable swipe-back gesture on pages with back buttons
  useSwipeBack();
  
  // Chat page has its own header - don't show global header
  const isChatPage = location.pathname === "/chat";

  // Track app opened
  useEffect(() => {
    const lastOpen = localStorage.getItem('last_app_open');
    const now = Date.now();
    const daysSinceLastOpen = lastOpen ? Math.floor((now - parseInt(lastOpen)) / (1000 * 60 * 60 * 24)) : null;
    const isReturningUser = !!lastOpen;
    
    Analytics.appOpened(isReturningUser, daysSinceLastOpen || undefined);
    localStorage.setItem('last_app_open', now.toString());
  }, []);

  // Check if tutorial should be shown
  useEffect(() => {
    if (!loading && profile) {
      const forceTutorial = localStorage.getItem('force_tutorial') === 'true';
      const tutorialCompleted = profile.tutorial_completed || localStorage.getItem('tutorial_completed') === 'true';

      if (forceTutorial || (!tutorialCompleted && profile.onboarding_completed)) {
        setShowTutorial(true);
      } else {
        setShowTutorial(false);
      }
    }
  }, [profile, loading, location.pathname]);

  return (
    <div className="h-screen w-full flex flex-col text-foreground overflow-hidden bg-gradient-app-bg relative" style={{ height: "100vh" }}>
      {/* Ambient color orbs */}
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'var(--ambient-orb-1)' }} />
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'var(--ambient-orb-2)' }} />
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'var(--ambient-orb-3)' }} />
      
      {/* Fixed header - hidden on Chat page which has its own header */}
      {!isChatPage && <Header />}

      {/* Main content area between header and bottom nav */}
      <main
        className="flex-1 w-full flex flex-col"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "none",
          WebkitOverflowScrolling: "touch",
          // Chat page handles its own top padding since it has custom header
          paddingTop: isChatPage 
            ? "0px"
            : `calc(${SAFE_AREA.top} + 56px)`,
          // Bottom nav height + safe area bottom; add keyboard height when open so inputs can scroll above it
          paddingBottom: isChatPage 
            ? "0px"
            : keyboardHeight > 0 
              ? `calc(56px + ${SAFE_AREA.bottom} + ${keyboardHeight}px)` 
              : `calc(56px + ${SAFE_AREA.bottom})`,
          paddingLeft: SAFE_AREA.left,
          paddingRight: SAFE_AREA.right,
          minHeight: 0,
        }}
      >
        <Outlet />
      </main>

      {/* Fixed bottom nav - hidden when keyboard is active so input can sit above keyboard */}
      {keyboardHeight === 0 && <BottomNavigation />}

      {/* Tutorial overlay */}
      {showTutorial && (
        <Tutorial onComplete={() => setShowTutorial(false)} />
      )}
    </div>
  );
};
